import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from '../HashRouter';
import type { VertexSetWithVertices, Preview, VertexSetUnionWithTarget } from '../../types';
import * as api from '../../services/api';
import { VertexSquareCard } from '../shared/VertexSquareCard';
import { VertexAutocomplete } from '../graph/VertexAutocomplete';
import { EditableNote } from '../EditableNote';
import { UnionsPanel } from './UnionsPanel';
import { CopyIcon, ShareIcon, DeleteIcon, RemoveIcon } from '../icons';
import { OutlinedButton } from '../shared/OutlinedButton';
// @ts-ignore
import { copyWithFeedback } from '../../../shared.js';

interface VertexSetDetailProps {
  setId: string;
  onClose: () => void;
}

interface VertexWithPreview {
  url: string;
  preview?: Preview;
}

export function VertexSetDetail({ setId, onClose }: VertexSetDetailProps) {
  const [setData, setSetData] = useState<VertexSetWithVertices | null>(null);
  const [vertices, setVertices] = useState<VertexWithPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingVertex, setAddingVertex] = useState(false);

  // Drag-and-drop state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  // Share state
  const [shareId, setShareId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const copyShareIdBtnRef = useRef<HTMLButtonElement>(null);

  // All user sets for unions panel autocomplete
  const [allUserSets, setAllUserSets] = useState<VertexSetWithVertices[]>([]);

  // Selected unions (for displaying their vertices)
  const [selectedUnions, setSelectedUnions] = useState<VertexSetUnionWithTarget[]>([]);

  // All unions (for showing /unions link)
  const [allUnions, setAllUnions] = useState<VertexSetUnionWithTarget[]>([]);

  useEffect(() => {
    loadSetData();
    loadAllUserSets();
  }, [setId]);

  function mkShareUrl(id: string): string {
    return `${window.location.origin}/vertex/set/${id}`;
  }

  async function loadAllUserSets() {
    try {
      const allSets = await api.fetchAllVertexSets();
      setAllUserSets(allSets);
    } catch (err) {
      console.error('Failed to load all user sets:', err);
    }
  }

  async function loadSetData() {
    try {
      setLoading(true);
      setError(null);

      const data = await api.fetchVertexSet(setId);

      setSetData(data);
      setShareId(data.shareId || null);

      if (data.shareId) {
        setShareUrl(mkShareUrl(data.shareId));
      } else {
        setShareUrl(null);
      }

      // Fetch previews for vertices in this set
      if (data.vertices.length > 0) {
        const urls = data.vertices.map((v) => v.vertex.rendered);
        const previewsData = await api.fetchPreviewsBatch(urls);
        setVertices(data.vertices.map((v) => ({
          url: v.vertex.rendered,
          preview: previewsData[v.vertex.rendered],
        })));
      } else {
        setVertices([]);
      }
    } catch (err) {
      console.error('Failed to load vertex set:', err);
      setError(err instanceof Error ? err.message : 'Failed to load vertex set');
    } finally {
      setLoading(false);
    }
  }

  // Save handlers for EditableNote
  async function handleSaveName(newValue: string | null) {
    if (!newValue?.trim()) return;
    const updated = await api.updateVertexSet(setId, { name: newValue.trim() });
    setSetData(updated);
  }

  async function handleSaveDescription(newValue: string | null) {
    const updated = await api.updateVertexSet(setId, { description: newValue });
    setSetData(updated);
  }

  // Sharing
  async function handleShareSet() {
    if (!setData) return;

    try {
      const id = await api.shareVertexSet(setId);
      setShareId(id);
      setShareUrl(mkShareUrl(id));
    } catch (err) {
      alert(`Failed to share: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleCopyShareLink() {
    if (!shareUrl || !copyBtnRef.current) return;
    await copyWithFeedback(copyBtnRef.current, shareUrl);
  }

  async function handleCopyShareId() {
    if (!shareId || !copyShareIdBtnRef.current) return;
    await copyWithFeedback(copyShareIdBtnRef.current, shareId);
  }

  async function handleAddVertex(vertexUrl: string) {
    if (!setData) return;

    try {
      await api.addVertexToSet(setId, vertexUrl);

      // Refresh the set data
      const updated = await api.fetchVertexSet(setId);
      setSetData(updated);

      // Update vertices with preview
      const previews = await api.fetchPreviewsBatch([vertexUrl]);
      setVertices((prev) => [...prev, { url: vertexUrl, preview: previews[vertexUrl] }]);

      setAddingVertex(false);
    } catch (err) {
      alert(`Failed to add vertex: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleDrop(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx || !setData) return;

    const movedUrl = vertices[fromIdx].url;

    // Optimistic reorder
    const reordered = [...vertices];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setVertices(reordered);

    // Determine the "before" or "after" relative vertex
    const move: api.MoveRequest = toIdx < fromIdx
      ? { move: movedUrl, to: { before: reordered[toIdx + 1]?.url ?? reordered[toIdx - 1]?.url } }
      : { move: movedUrl, to: { after: reordered[toIdx - 1]?.url ?? reordered[toIdx + 1]?.url } };

    try {
      const updatedElems = await api.reorderVertexInSet(setId, move);
      // Sync with server response
      const urls = updatedElems.map(e => e.vertex.rendered);
      setVertices(prev => {
        const previewMap = new Map(prev.map(v => [v.url, v.preview]));
        return urls.map(url => ({ url, preview: previewMap.get(url) }));
      });
      setSetData(prev => prev ? { ...prev, vertices: updatedElems } : null);
    } catch (err) {
      console.error('Failed to reorder:', err);
      // Revert on failure
      loadSetData();
    }
  }

  async function handleRemoveVertex(vertexUrl: string) {
    if (!setData) return;

    try {
      await api.removeVertexFromSet(setId, vertexUrl);

      // Update local state
      setSetData((prev) => prev ? {
        ...prev,
        vertices: prev.vertices.filter((v) => v.vertex.rendered !== vertexUrl),
      } : null);
      setVertices((prev) => prev.filter((v) => v.url !== vertexUrl));
    } catch (err) {
      alert(`Failed to remove vertex: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  if (loading) {
    return (
      <div class="loading" style="padding: 60px 20px; color: #6c757d; font-size: 18px;">
        Loading set...
      </div>
    );
  }

  if (error || !setData) {
    return (
      <div class="error-message" style="padding: 20px; color: #dc3545;">
        {error || 'Set not found'}
      </div>
    );
  }

  function handleUnionError(message: string) {
    alert(message);
  }

  return (
    <div class="vertex-set-detail">
      <div class="vertex-set-detail-top">
        <div class="vertex-set-detail-header-section">
          <div class="vertex-set-detail-header">
            <button class="btn-back" onClick={onClose}>
              ← Back
            </button>

            <div class="vertex-set-detail-info">
              <EditableNote
                value={setData.vertexSet.name}
                placeholder="Set name..."
                className="vertex-set-detail-name"
                onSave={handleSaveName}
                singleLine={true}
              />
              <EditableNote
                value={setData.vertexSet.description}
                placeholder="Add a description..."
                className="vertex-set-detail-description"
                onSave={handleSaveDescription}
              />
            </div>
          </div>

          {/* Share Section */}
          <div class="vertex-set-share-section">
            {shareId && shareUrl ? (
              <div class="shared-link-row">
                <button ref={copyBtnRef} onClick={handleCopyShareLink} class="btn-copy" title="Copy share link">
                  <CopyIcon />
                </button>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="share-link-anchor"
                  title="Open shared set"
                >
                  {shareUrl}
                </a>
                {allUnions.length > 0 && (
                  <>
                    <span class="share-link-separator">+</span>
                    <a
                      href={`${shareUrl}/unions`}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="share-link-unions"
                      title="View this set combined with its unions"
                    >
                      /unions
                    </a>
                  </>
                )}
                <button
                  ref={copyShareIdBtnRef}
                  onClick={handleCopyShareId}
                  class="btn-share-for-unions"
                  title="Copy the ShareId to share with others, so they can add this set to their unions"
                >
                  Share for unions
                </button>
              </div>
            ) : (
              <OutlinedButton onClick={handleShareSet}>
                <ShareIcon />
                Share
              </OutlinedButton>
            )}
          </div>
        </div>

        <div class="vertex-set-detail-sidebar">
          <UnionsPanel
            currentSetId={setId}
            currentShareId={shareId}
            allUserSets={allUserSets}
            onError={handleUnionError}
            onSelectionChange={setSelectedUnions}
            onUnionsLoaded={setAllUnions}
          />
        </div>
      </div>

      <div class="vertex-set-detail-content">
          <div class="vertex-set-vertices-header">
            <span class="vertex-set-vertex-count">
              {vertices.length} {vertices.length === 1 ? 'vertex' : 'vertices'}
            </span>
            {!addingVertex && (
              <button class="btn-add-vertex" onClick={() => setAddingVertex(true)}>
                + Add Vertex
              </button>
            )}
          </div>

          {addingVertex && (
            <VertexAutocomplete
              excludeUrls={setData?.vertices.map((v) => v.vertex.rendered) || []}
              skipInfoStep={true}
              onSelect={(url) => handleAddVertex(url)}
              onCancel={() => setAddingVertex(false)}
            />
          )}

          {vertices.length === 0 && selectedUnions.length === 0 ? (
            <div class="empty-state" style="padding: 40px 20px; text-align: center; color: #777;">
              <p>No vertices in this set.</p>
              <p class="subtitle">Add vertices using the button above.</p>
            </div>
          ) : (
            <div class="vertex-set-grid">
              {/* Current set's vertices (draggable) */}
              {vertices.map((v, i) => (
                <div
                  key={v.url}
                  draggable
                  class={`vertex-set-drag-wrapper${dragIndex === i ? ' dragging' : ''}${dropIndex === i ? ' drop-target' : ''}`}
                  onDragStart={(e) => {
                    setDragIndex(i);
                    (e as DragEvent).dataTransfer!.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    (e as DragEvent).dataTransfer!.dropEffect = 'move';
                    setDropIndex(i);
                  }}
                  onDragLeave={() => {
                    if (dropIndex === i) setDropIndex(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragIndex !== null) handleDrop(dragIndex, i);
                    setDragIndex(null);
                    setDropIndex(null);
                  }}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setDropIndex(null);
                  }}
                >
                  <VertexSquareCard
                    url={v.url}
                    preview={v.preview}
                    onClick={() => route(`/archive/${encodeURIComponent(v.url)}?from=${encodeURIComponent(`/vertex-sets/${setId}`)}`)}
                    actions={
                      <button
                        class="graph-edge-card-delete"
                        title="Remove from set"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVertex(v.url);
                        }}
                      >
                        <RemoveIcon />
                      </button>
                    }
                  />
                </div>
              ))}
              {/* Vertices from selected unions */}
              {selectedUnions.flatMap((unionData) =>
                unionData.target.vertexCards.map((card) => (
                  <VertexSquareCard
                    key={`${unionData.union.targetShareId}-${card.vertexSetElem.vertex.rendered}`}
                    url={card.vertexSetElem.vertex.rendered}
                    preview={card.preview || undefined}
                    onClick={() => route(`/archive/${encodeURIComponent(card.vertexSetElem.vertex.rendered)}?from=${encodeURIComponent(`/vertex-sets/${setId}`)}`)}
                    fromUnion={true}
                  />
                ))
              )}
            </div>
          )}
        </div>
    </div>
  );
}
